import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Settings,
  CreditCard,
  Bell,
  Shield,
  Globe,
  Palette,
  Save,
  RefreshCw,
  Key,
  Mail,
  MessageSquare,
  Smartphone,
  DollarSign,
  Percent,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import {
  Card,
  Button,
  ShimmerButton,
  Input,
  Textarea,
  Select,
  Switch,
  cn,
  useToast,
} from '@raffle/ui';

interface SettingsSection {
  id: string;
  title: string;
  icon: React.ReactNode;
}

const sections: SettingsSection[] = [
  { id: 'general', title: 'General', icon: <Settings className="w-5 h-5" /> },
  { id: 'payment', title: 'Payment', icon: <CreditCard className="w-5 h-5" /> },
  { id: 'notifications', title: 'Notifications', icon: <Bell className="w-5 h-5" /> },
  { id: 'security', title: 'Security', icon: <Shield className="w-5 h-5" /> },
];

export const SettingsPage: React.FC = () => {
  const toast = useToast();
  const [activeSection, setActiveSection] = useState('general');
  const [isSaving, setIsSaving] = useState(false);

  // General Settings
  const [generalSettings, setGeneralSettings] = useState({
    platformName: 'RaffleHub',
    supportEmail: 'support@rafflehub.com',
    supportPhone: '+234 800 000 0000',
    timezone: 'Africa/Lagos',
    currency: 'NGN',
    maintenanceMode: false,
  });

  // Payment Settings
  const [paymentSettings, setPaymentSettings] = useState({
    paystackPublicKey: 'pk_live_xxxxx',
    paystackSecretKey: '••••••••••••••••',
    flutterwavePublicKey: 'FLWPUBK-xxxxx',
    flutterwaveSecretKey: '••••••••••••••••',
    minTicketPrice: 10000,
    maxTicketsPerPurchase: 100,
    platformFeePercent: 5,
    refundWindow: 24,
  });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailEnabled: true,
    smsEnabled: true,
    pushEnabled: false,
    ticketPurchaseEmail: true,
    ticketPurchaseSms: false,
    drawReminderEmail: true,
    drawReminderSms: true,
    winnerNotificationEmail: true,
    winnerNotificationSms: true,
    marketingEmail: false,
  });

  // Security Settings
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorRequired: false,
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    passwordMinLength: 8,
    requireSpecialChar: true,
    requireNumber: true,
    ipWhitelist: '',
  });

  const handleSave = async () => {
    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    toast.success('Settings Saved', 'Your changes have been saved successfully');
    setIsSaving(false);
  };

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-display text-lg font-semibold text-text-primary mb-4">
          Platform Information
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Platform Name"
            value={generalSettings.platformName}
            onChange={(e) =>
              setGeneralSettings((prev) => ({ ...prev, platformName: e.target.value }))
            }
          />
          <Select
            label="Timezone"
            value={generalSettings.timezone}
            onChange={(e) =>
              setGeneralSettings((prev) => ({ ...prev, timezone: e.target.value }))
            }
            options={[
              { value: 'Africa/Lagos', label: 'Africa/Lagos (WAT)' },
              { value: 'UTC', label: 'UTC' },
              { value: 'Europe/London', label: 'Europe/London (GMT)' },
            ]}
          />
          <Input
            label="Support Email"
            type="email"
            value={generalSettings.supportEmail}
            onChange={(e) =>
              setGeneralSettings((prev) => ({ ...prev, supportEmail: e.target.value }))
            }
            leftIcon={<Mail className="w-5 h-5" />}
          />
          <Input
            label="Support Phone"
            value={generalSettings.supportPhone}
            onChange={(e) =>
              setGeneralSettings((prev) => ({ ...prev, supportPhone: e.target.value }))
            }
            leftIcon={<Smartphone className="w-5 h-5" />}
          />
        </div>
      </div>

      <div className="border-t border-white/10 pt-6">
        <h3 className="font-display text-lg font-semibold text-text-primary mb-4">
          Maintenance
        </h3>
        <div className="p-4 rounded-vault bg-surface-2 border border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-vault bg-vault-warning/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-vault-warning" />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Maintenance Mode</p>
                <p className="text-xs text-text-muted">
                  Temporarily disable the platform for users
                </p>
              </div>
            </div>
            <Switch
              checked={generalSettings.maintenanceMode}
              onChange={(checked) =>
                setGeneralSettings((prev) => ({ ...prev, maintenanceMode: checked }))
              }
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderPaymentSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-display text-lg font-semibold text-text-primary mb-4">
          Paystack Configuration
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Public Key"
            value={paymentSettings.paystackPublicKey}
            onChange={(e) =>
              setPaymentSettings((prev) => ({ ...prev, paystackPublicKey: e.target.value }))
            }
            leftIcon={<Key className="w-5 h-5" />}
          />
          <Input
            label="Secret Key"
            type="password"
            value={paymentSettings.paystackSecretKey}
            onChange={(e) =>
              setPaymentSettings((prev) => ({ ...prev, paystackSecretKey: e.target.value }))
            }
            leftIcon={<Key className="w-5 h-5" />}
          />
        </div>
      </div>

      <div className="border-t border-white/10 pt-6">
        <h3 className="font-display text-lg font-semibold text-text-primary mb-4">
          Flutterwave Configuration
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Public Key"
            value={paymentSettings.flutterwavePublicKey}
            onChange={(e) =>
              setPaymentSettings((prev) => ({ ...prev, flutterwavePublicKey: e.target.value }))
            }
            leftIcon={<Key className="w-5 h-5" />}
          />
          <Input
            label="Secret Key"
            type="password"
            value={paymentSettings.flutterwaveSecretKey}
            onChange={(e) =>
              setPaymentSettings((prev) => ({ ...prev, flutterwaveSecretKey: e.target.value }))
            }
            leftIcon={<Key className="w-5 h-5" />}
          />
        </div>
      </div>

      <div className="border-t border-white/10 pt-6">
        <h3 className="font-display text-lg font-semibold text-text-primary mb-4">
          Transaction Rules
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Minimum Ticket Price (₦)
            </label>
            <div className="relative">
              <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
              <input
                type="number"
                value={paymentSettings.minTicketPrice}
                onChange={(e) =>
                  setPaymentSettings((prev) => ({
                    ...prev,
                    minTicketPrice: Number(e.target.value),
                  }))
                }
                className="w-full h-12 pl-12 pr-4 bg-surface-2 border border-white/10 rounded-vault font-mono text-text-primary focus:outline-none focus:border-gold-500/50"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Max Tickets Per Purchase
            </label>
            <input
              type="number"
              value={paymentSettings.maxTicketsPerPurchase}
              onChange={(e) =>
                setPaymentSettings((prev) => ({
                  ...prev,
                  maxTicketsPerPurchase: Number(e.target.value),
                }))
              }
              className="w-full h-12 px-4 bg-surface-2 border border-white/10 rounded-vault font-mono text-text-primary focus:outline-none focus:border-gold-500/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Platform Fee (%)
            </label>
            <div className="relative">
              <Percent className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
              <input
                type="number"
                value={paymentSettings.platformFeePercent}
                onChange={(e) =>
                  setPaymentSettings((prev) => ({
                    ...prev,
                    platformFeePercent: Number(e.target.value),
                  }))
                }
                className="w-full h-12 pl-12 pr-4 bg-surface-2 border border-white/10 rounded-vault font-mono text-text-primary focus:outline-none focus:border-gold-500/50"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Refund Window (hours)
            </label>
            <div className="relative">
              <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
              <input
                type="number"
                value={paymentSettings.refundWindow}
                onChange={(e) =>
                  setPaymentSettings((prev) => ({
                    ...prev,
                    refundWindow: Number(e.target.value),
                  }))
                }
                className="w-full h-12 pl-12 pr-4 bg-surface-2 border border-white/10 rounded-vault font-mono text-text-primary focus:outline-none focus:border-gold-500/50"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-display text-lg font-semibold text-text-primary mb-4">
          Notification Channels
        </h3>
        <div className="space-y-3">
          {[
            { key: 'emailEnabled', label: 'Email Notifications', desc: 'Send notifications via email' },
            { key: 'smsEnabled', label: 'SMS Notifications', desc: 'Send notifications via SMS' },
            { key: 'pushEnabled', label: 'Push Notifications', desc: 'Send push notifications to mobile app' },
          ].map((item) => (
            <div
              key={item.key}
              className="p-4 rounded-vault bg-surface-2 border border-white/10 flex items-center justify-between"
            >
              <div>
                <p className="text-sm font-medium text-text-primary">{item.label}</p>
                <p className="text-xs text-text-muted">{item.desc}</p>
              </div>
              <Switch
                checked={notificationSettings[item.key as keyof typeof notificationSettings] as boolean}
                onChange={(checked) =>
                  setNotificationSettings((prev) => ({ ...prev, [item.key]: checked }))
                }
              />
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-white/10 pt-6">
        <h3 className="font-display text-lg font-semibold text-text-primary mb-4">
          Notification Events
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Event
                </th>
                <th className="py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">
                  Email
                </th>
                <th className="py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">
                  SMS
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {[
                { event: 'Ticket Purchase', emailKey: 'ticketPurchaseEmail', smsKey: 'ticketPurchaseSms' },
                { event: 'Draw Reminder', emailKey: 'drawReminderEmail', smsKey: 'drawReminderSms' },
                { event: 'Winner Notification', emailKey: 'winnerNotificationEmail', smsKey: 'winnerNotificationSms' },
              ].map((row) => (
                <tr key={row.event}>
                  <td className="py-4 text-sm text-text-primary">{row.event}</td>
                  <td className="py-4 text-center">
                    <Switch
                      checked={notificationSettings[row.emailKey as keyof typeof notificationSettings] as boolean}
                      onChange={(checked) =>
                        setNotificationSettings((prev) => ({ ...prev, [row.emailKey]: checked }))
                      }
                    />
                  </td>
                  <td className="py-4 text-center">
                    <Switch
                      checked={notificationSettings[row.smsKey as keyof typeof notificationSettings] as boolean}
                      onChange={(checked) =>
                        setNotificationSettings((prev) => ({ ...prev, [row.smsKey]: checked }))
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-display text-lg font-semibold text-text-primary mb-4">
          Authentication
        </h3>
        <div className="space-y-3">
          <div className="p-4 rounded-vault bg-surface-2 border border-white/10 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-text-primary">Require 2FA for Admins</p>
              <p className="text-xs text-text-muted">
                All admin accounts must enable two-factor authentication
              </p>
            </div>
            <Switch
              checked={securitySettings.twoFactorRequired}
              onChange={(checked) =>
                setSecuritySettings((prev) => ({ ...prev, twoFactorRequired: checked }))
              }
            />
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 pt-6">
        <h3 className="font-display text-lg font-semibold text-text-primary mb-4">
          Session & Login
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Session Timeout (minutes)
            </label>
            <input
              type="number"
              value={securitySettings.sessionTimeout}
              onChange={(e) =>
                setSecuritySettings((prev) => ({
                  ...prev,
                  sessionTimeout: Number(e.target.value),
                }))
              }
              className="w-full h-12 px-4 bg-surface-2 border border-white/10 rounded-vault font-mono text-text-primary focus:outline-none focus:border-gold-500/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Max Login Attempts
            </label>
            <input
              type="number"
              value={securitySettings.maxLoginAttempts}
              onChange={(e) =>
                setSecuritySettings((prev) => ({
                  ...prev,
                  maxLoginAttempts: Number(e.target.value),
                }))
              }
              className="w-full h-12 px-4 bg-surface-2 border border-white/10 rounded-vault font-mono text-text-primary focus:outline-none focus:border-gold-500/50"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 pt-6">
        <h3 className="font-display text-lg font-semibold text-text-primary mb-4">
          Password Policy
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Minimum Password Length
            </label>
            <input
              type="number"
              value={securitySettings.passwordMinLength}
              onChange={(e) =>
                setSecuritySettings((prev) => ({
                  ...prev,
                  passwordMinLength: Number(e.target.value),
                }))
              }
              className="w-full h-12 px-4 bg-surface-2 border border-white/10 rounded-vault font-mono text-text-primary focus:outline-none focus:border-gold-500/50"
            />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Require Special Character</span>
              <Switch
                checked={securitySettings.requireSpecialChar}
                onChange={(checked) =>
                  setSecuritySettings((prev) => ({ ...prev, requireSpecialChar: checked }))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Require Number</span>
              <Switch
                checked={securitySettings.requireNumber}
                onChange={(checked) =>
                  setSecuritySettings((prev) => ({ ...prev, requireNumber: checked }))
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 pt-6">
        <h3 className="font-display text-lg font-semibold text-text-primary mb-4">
          IP Whitelist
        </h3>
        <Textarea
          value={securitySettings.ipWhitelist}
          onChange={(e) =>
            setSecuritySettings((prev) => ({ ...prev, ipWhitelist: e.target.value }))
          }
          placeholder="Enter IP addresses, one per line (leave empty to allow all)"
          rows={4}
        />
        <p className="text-xs text-text-muted mt-2">
          Restrict admin panel access to specific IP addresses
        </p>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return renderGeneralSettings();
      case 'payment':
        return renderPaymentSettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'security':
        return renderSecuritySettings();
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-text-primary">Settings</h1>
          <p className="text-text-secondary">Configure platform settings and preferences</p>
        </div>
        <ShimmerButton onClick={handleSave} loading={isSaving}>
          <Save className="w-4 h-4 mr-2" />
          Save Changes
        </ShimmerButton>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Sidebar */}
        <Card className="p-4 lg:col-span-1 h-fit">
          <nav className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-3 rounded-vault text-left transition-colors',
                  activeSection === section.id
                    ? 'bg-gold-500/10 text-gold-500'
                    : 'text-text-secondary hover:text-text-primary hover:bg-surface-3'
                )}
              >
                {section.icon}
                <span className="text-sm font-medium">{section.title}</span>
              </button>
            ))}
          </nav>
        </Card>

        {/* Content */}
        <Card className="p-6 lg:col-span-3">{renderContent()}</Card>
      </div>
    </div>
  );
};
